import type { ReactNode } from "react";
import { isPlayable, type NewsItem, type StationV2 } from "@orvis/core";
import { useTranslation } from "react-i18next";
import { openArticle } from "../openArticle.js";

/**
 * Small presentational primitives. Reused across the four tabs.
 * Nothing here does network I/O — every card is a pure function of its
 * props.
 */

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`bg-card border border-border rounded-lg p-4 ${className}`}>
      {children}
    </div>
  );
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h2 className="text-xs uppercase tracking-wider text-text/60 px-1 mb-2">
      {children}
    </h2>
  );
}

export function LoadingRow({ label }: { label?: string }) {
  return (
    <div
      className="animate-pulse text-text/60 text-sm px-4 py-6 text-center"
      role="status"
      aria-live="polite"
    >
      {label ?? "…"}
    </div>
  );
}

export function NotLive() {
  const { t } = useTranslation();
  return (
    <Card className="text-center text-text/70 my-4">
      {t("station_offline") /* used as a generic "the whole tab is offline" message */}
    </Card>
  );
}

export function EmptyRow({ label }: { label: string }) {
  return (
    <div className="text-sm text-text/60 px-1 py-4 text-center">
      {label}
    </div>
  );
}

export function NewsCard({ item, action }: { item: NewsItem; action?: ReactNode }) {
  const { t } = useTranslation();
  const time = relativeTime(item.publishedAt, t);
  return (
    <div className="relative">
    <a
      href={item.url}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => {
        if (openArticle(item.url, item.source)) e.preventDefault();
      }}
      className="block hover:bg-card/60 focus-visible:bg-card/60 rounded-lg"
    >
      <Card className="flex gap-3">
        {item.image ? (
          <img
            src={item.image}
            alt=""
            loading="lazy"
            className="w-16 h-16 object-cover rounded flex-shrink-0"
          />
        ) : null}
        <div className="min-w-0">
          <h3 className="font-semibold leading-tight line-clamp-2">{item.title}</h3>
          {item.description ? (
            <p className="text-sm text-text/70 mt-1 line-clamp-2">{item.description}</p>
          ) : null}
          {item.source || time ? (
            <p className={`text-xs text-text/50 mt-1 truncate ${action ? "pr-28" : ""}`}>
              {[item.source, time].filter(Boolean).join(" · ")}
            </p>
          ) : null}
        </div>
      </Card>
    </a>
    {/* Kept outside the <a> — a button nested in a link isn't valid HTML. */}
    {action ? <div className="absolute right-3 bottom-3">{action}</div> : null}
    </div>
  );
}

function relativeTime(iso: string | undefined, t: (k: string, o?: Record<string, unknown>) => string): string | undefined {
  if (!iso) return undefined;
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return undefined;
  const mins = Math.round((Date.now() - at) / 60_000);
  if (mins < 1) return t("time_just_now");
  if (mins < 60) return t("time_minutes_ago", { count: mins });
  const hours = Math.round(mins / 60);
  if (hours < 24) return t("time_hours_ago", { count: hours });
  return t("time_days_ago", { count: Math.round(hours / 24) });
}

interface StationCardProps {
  station: StationV2;
  onSelect: () => void;
  /** Show "Live" for playable stations. "Offline" always shows when the last health check failed. */
  showLiveBadge?: boolean;
  /** Secondary line (e.g. region) shown under the name. */
  subtitle?: string;
  selected?: boolean;
  /** When defined, renders a heart button; leaves it out otherwise (Discover rows do). */
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
}

export function StationCard({
  station,
  onSelect,
  showLiveBadge = false,
  subtitle,
  selected = false,
  isFavorite,
  onToggleFavorite,
}: StationCardProps) {
  const { t } = useTranslation();
  const playable = isPlayable(station);
  return (
    <div
      className={
        `w-full flex items-center gap-3 bg-card border border-border rounded-lg p-3 ` +
        `hover:border-accent focus-within:border-accent transition-colors ` +
        (selected ? "border-accent " : "") +
        (playable ? "" : "opacity-60")
      }
    >
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className="flex-1 flex items-center gap-3 text-left min-w-0"
      >
        <StationLogo logo={station.logo} name={station.name} />
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate">{station.name}</div>
          {showLiveBadge || !playable || subtitle ? (
            <div className="text-xs truncate">
              {showLiveBadge || !playable ? (
                <span className={playable ? "text-emerald-400" : "text-text/50"}>
                  {playable ? t("live") : t("offline")}
                </span>
              ) : null}
              {subtitle ? (
                <span className="text-text/60">
                  {showLiveBadge || !playable ? " · " : ""}
                  {subtitle}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </button>
      {typeof isFavorite === "boolean" && onToggleFavorite ? (
        <button
          type="button"
          onClick={onToggleFavorite}
          className="p-2 text-text/70 hover:text-accent focus-visible:text-accent"
          aria-pressed={isFavorite}
          aria-label={t("favorites")}
        >
          {isFavorite ? heartFilled : heartOutline}
        </button>
      ) : null}
    </div>
  );
}

function StationLogo({ logo, name }: { logo: string; name: string }) {
  if (!logo) {
    return (
      <div className="w-12 h-12 rounded bg-border flex items-center justify-center text-text/60 flex-shrink-0">
        {name.slice(0, 1)}
      </div>
    );
  }
  return (
    <img
      src={logo}
      alt=""
      loading="lazy"
      className="w-12 h-12 rounded object-contain bg-black/30 flex-shrink-0"
    />
  );
}

const heartOutline = (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78Z" />
  </svg>
);

const heartFilled = (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" stroke="currentColor" strokeWidth={2} aria-hidden>
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78Z" />
  </svg>
);
